import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { QueryParams } from "./QueryParams";
import { ChangeEvent, createRef, JSX } from "react";
import { QueryParamsHelper } from "../../../../Common/QueryParamsHelper";
import { LanguageValuesHelper } from "@iconics/localisation";
import { Guid, PagedResponse, TypeHelper, Validator } from "@iconics/core";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import { SerendieSymbol } from "@serendie/symbols";
import { ImportResultWrapper, NodeData } from "./DataModels";
import { RootNodeId } from "../../../../Providers.StaticData/StaticDataProvider";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { AlertDialog, Button, ButtonBar, Card, CheckBox, Dialog, DialogContent, DialogFooter, FileDropZone, GridContainer, GridItem, HelperText, PasswordField, RightButtons, Select } from "@iconics/components";
import { Accept } from "react-dropzone/.";
import { Tooltip } from "@serendie/ui";

export class NodesImportDialog extends IbssComponent<IProps, IState>
{
    private labels = appContext().labels;
    private validator = new Validator(appContext().core.validationOptions);
    private fileTypes = [
        { label: this.labels.ImportFormat_GenesisAssetCatalogue, value: 'Genesis'},
        { label: this.labels.ImportFormat_NodesConfiguration, value: 'Nodes'},
        { label: this.labels.ImportFormat_ControlPlan, value: 'Control'},
    ];

    constructor(props: IProps)
    {
        super(props);

        this.state =
        {
            selectedFileTypes: [this.fileTypes[0].value],
            fileTypeError: '',
            selectedNodes: [],
            nodeError: '',
            file: undefined,
            updateExisting: false,
            continueOnError: true,
            options: [],
            uploadPath: '',
            genesis: false,
            // format: '',
            allowedFileTypes: {},
            resetFilesToken: Guid.new().toString(),
            showResult: false,
            partialSuccess: false
        };
    }

    public async componentDidMount()
    {
        this.validator
            .addInput('fileType', () => this.state.selectedFileTypes[0] ?? '', i => this.setState({ fileTypeError: i }))
            .isRequired();
        this.validator
            .addInput('node', () => this.state.selectedNodes[0] ?? '', i => this.setState({ nodeError: i }))
            .isRequired();
    }

    public componentDidUpdate(prevProps: IProps)
    {
        if (prevProps.open != this.props.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        const defaultFileType = this.fileTypes[0].value;

        this.setState({
            selectedFileTypes: [defaultFileType],
            fileTypeError: '',
            selectedNodes: this.props.nodeId ? [this.props.nodeId.toString()] : [],
            nodeError: '',
            nodeId: this.props.nodeId,
            file: undefined,
            updateExisting: false,
            continueOnError: true,
            options: [],
            uploadPath: '',
            genesis: false,
            // format: '',
            allowedFileTypes: {},
            resetFilesToken: Guid.new().toString(),
            showResult: false
        })
        this.onFileTypeChanged(defaultFileType);
    }

    private onFileUploaded(file: File | null): void
    {
        this.setState({
            file: file ?? undefined,
        });
    }

    private async handleImport(): Promise<void>
    {
        if (!this.state.file)
            return;
        const formData = new FormData();
        formData.append('file', this.state.file);

        await this.loading(async () =>
        {
            if (!this.state.nodeId) return;

            const result = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.import.post<ImportResultWrapper>({
                nodeId: this.state.nodeId,
                genesis: this.state.genesis, // TODO add format, need to wait for API changes
                tenancy: appContext().tenantId,
                body: formData,
            });
            console.log(`Import result:\n${JSON.stringify(result)}`);

            let partialSuccess = false;
            if (result.Failure.Equipments.length === 0 ||
                result.Failure.EquipmentProperties.length === 0 ||
                result.Failure.TreeViews.length === 0)

            {
                partialSuccess = true;
            }

            this.setState({ showResult: true, partialSuccess: partialSuccess });
        });
    }

    private onFileTypeChanged(value: string): void
    {
        let genesis = false;
        // let format = ''; TODO need to wait for API changes
        let allowedFileTypes = {}
        switch (value)
        {
            case 'Genesis':
                genesis = true;
                // format = 'GenesisAssetCatalogue';
                allowedFileTypes = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                break;
            case 'Nodes':
                genesis = false;
                // format = 'NodesConfiguration';
                allowedFileTypes = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                break;
            case 'Control':
                genesis = false;
                // format = 'ControlPlan';
                allowedFileTypes = { 'text/json': ['.json'] }
                break;
            default:
                break;
        }
        this.setState({ 
            selectedFileTypes: [value],
            genesis: genesis,
            // format: format,
            allowedFileTypes: allowedFileTypes
        }, () => this.validator.validate('fileType'));
    }
    private handleResultDialogClose(): void
    {
        this.setState({
            showResult: false
        })
        this.props.onClose();
    }

    public render(): JSX.Element
    {
        return (
            <>
                <Dialog
                    isOpen={this.props.open}
                    onOpenChange={() => this.props.onClose()}
                >
                    <DialogContent>
                        <Card
                            title={this.labels.Platform_Nodes_ImportDialog_Title}
                            subTitle={this.labels.Platform_Nodes_ImportDialog_SubTitle}
                        >
                            <GridContainer>
                                <GridItem>
                                    <Select
                                        label={this.labels.Platform_Nodes_ImportDialog_FileType}
                                        placeholder={this.labels.Platform_Nodes_ImportDialog_FileType}
                                        value={this.state.selectedFileTypes}
                                        onSelect={e => this.onFileTypeChanged(e.value)}
                                        items={this.fileTypes.map(x => ({ label: x.label, value: x.value.toString() }))}
                                        required
                                        invalid={!!this.state.fileTypeError}
                                        invalidMessage={this.state.fileTypeError}
                                    />
                                </GridItem>
                                <GridItem>
                                    <Select
                                        label={this.labels.Platform_Nodes_ImportDialog_Node}
                                        placeholder={this.labels.Platform_Nodes_ImportDialog_Node}
                                        value={this.state.selectedNodes}
                                        onSelect={e => this.setState({ selectedNodes: [e.value] }, () => this.validator.validate('node'))}
                                        items={this.props.nodes.map(x => ({ label: x.label, value: x.id.toString() }))}
                                        required
                                        disabled={this.props.nodeId !== undefined}
                                        invalid={!!this.state.nodeError}
                                        invalidMessage={this.state.nodeError}
                                    />
                                </GridItem>
                                <GridItem expanded={6}>
                                    <Card
                                        title={this.labels.Platform_Nodes_ImportDialog_FileDropZone_Title}
                                        subTitle={this.labels.Platform_Nodes_ImportDialog_FileDropZone_SubTitle}
                                    >
                                        <FileDropZone
                                            existingFileNames={this.state.options}
                                            uploadPath={this.state.uploadPath}
                                            allowedFileTypes={this.state.allowedFileTypes}
                                            onFilesDropped={(file) => this.onFileUploaded(file)}
                                            previewFileOnDrop={true}
                                            resetFilesToken={this.state.resetFilesToken}
                                        />
                                    </Card>
                                </GridItem>
                                <GridItem expanded={6}>
                                    <Card
                                        title={this.labels.Platform_Nodes_ImportDialog_ImportOptions_Title}
                                    >
                                        <GridContainer>
                                            <GridItem>
                                                <CheckBox
                                                    label={this.labels.Platform_Nodes_ImportDialog_UpdateExisting}

                                                    checked={this.state.updateExisting}
                                                    onCheckedChange={(e) => this.setState({ updateExisting: e.checked as boolean })}
                                                    helperText={this.labels.Platform_Nodes_ImportDialog_UpdateExisting_HelperText}
                                                />
                                            </GridItem>
                                            <GridItem>
                                                <CheckBox
                                                    label={this.labels.Platform_Nodes_ImportDialog_ContinueOnError}
                                                    checked={this.state.continueOnError}
                                                    onCheckedChange={(e) => this.setState({ continueOnError: e.checked as boolean })}
                                                    helperText={this.labels.Platform_Nodes_ImportDialog_ContinueOnError_HelperText}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </Card>
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.onClose()}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    styleType='filled'
                                    onClick={() => this.handleImport()}
                                    disabled={!this.validator.isValid}
                                >
                                    {this.labels.Platform_AssetWorX_Import}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
                <AlertDialog
                    show={this.state.showResult}
                    title={this.state.partialSuccess ? this.labels.Platform_AssetWorX_ImportPartialSuccess :
                        this.labels.Platform_AssetWorX_ImportSuccess}
                    message={this.state.partialSuccess ? this.labels.Platform_AssetWorX_ImportPartialSuccess_Message :
                        this.labels.Platform_AssetWorX_ImportSuccess_Message}
                    onClose={() => this.handleResultDialogClose()}
                />
            </>
        );
    }
}

export interface IProps 
{ 
    nodeId?: number;
    open: boolean;
    nodes: {id: number, label: string}[];
    onClose: () => void;
}

export interface IState
{
    selectedFileTypes: string[];
    fileTypeError: string;
    selectedNodes: string[];
    nodeId?: number;
    nodeError: string;
    file: File | undefined;
    updateExisting: boolean;
    continueOnError: boolean;
    options: string[];
    uploadPath: string;
    genesis: boolean;
    // format: string;
    allowedFileTypes: Accept;
    resetFilesToken: string;
    showResult: boolean;
    partialSuccess: boolean;
}
